/**
 * The last account that signed in successfully, on THIS browser.
 *
 * Why it exists: Auth0 keeps its own SSO session, so pressing "Iniciar sesión"
 * often skips the login form entirely and goes straight back — which is
 * pleasant when it works and baffling when it does not, because the operator
 * never sees which account was used. The console cannot ask Auth0 who that is:
 * before a successful login there is no token and therefore no identity. So we
 * remember the last one that worked and name it on the button.
 *
 * It is a convenience, never a fact to rely on: it is per-browser, it is absent
 * until the first successful login, and a viewer can clear it at any time. It
 * is NOT an authorization signal and must never gate anything — Auth0's session
 * is the only thing that decides whether the next click actually signs anyone
 * in. Nothing here is a secret: it is the operator's own address, already shown
 * in the app's header once they are in.
 *
 * Every accessor is wrapped: `localStorage` throws on access in a private
 * window and in embedded contexts with site data blocked, and an exception here
 * would take the sign-in screen down with it.
 */
const KEY = 'circa.lastAccount';

export function rememberAccount(email: string | undefined): void {
  if (!email) return;
  try {
    localStorage.setItem(KEY, email);
  } catch {
    // Storage unavailable — the button just falls back to its generic label.
  }
}

export function lastAccount(): string | null {
  try {
    return localStorage.getItem(KEY);
  } catch {
    return null;
  }
}

export function forgetAccount(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    // Nothing to do: the caller is already sending them to a fresh login.
  }
}
